import React, { useState } from 'react'
import { Layout, Menu, Input, Button, Avatar, Dropdown, Space } from 'antd'
import type { MenuProps } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  HomeOutlined,
  BookOutlined,
  MedicineBoxOutlined,
  ToolOutlined,
  UserOutlined,
  LogoutOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  AppstoreOutlined,
  IdcardOutlined,
} from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store'
import { logout } from '../../store/slices/authSlice'
import { getRuoyiAdminIndexUrl } from '../../utils/ruoyiAdminUrl'
import './AppHeader.css'

const { Header } = Layout

const AppHeader: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)
  // 根据当前路径确定选中的菜单项
  const getCurrentKey = () => {
    const path = location.pathname
    if (path === '/') return 'home'
    if (path.startsWith('/knowledge')) return 'knowledge'
    if (path.startsWith('/diagnosis')) return 'diagnosis'
    if (path.startsWith('/tools')) return 'tools'
    if (path.startsWith('/about')) return 'about'
    return 'home'
  }

  const [current, setCurrent] = useState(getCurrentKey())
  const [searchValue, setSearchValue] = useState('')

  // 监听路由变化更新选中状态
  React.useEffect(() => {
    setCurrent(getCurrentKey())
  }, [location.pathname])

  const menuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: '首页',
      onClick: () => navigate('/'),
    },
    {
      key: 'knowledge',
      icon: <BookOutlined />,
      label: '中医药知识树',
      onClick: () => navigate('/knowledge'),
    },
    {
      key: 'diagnosis',
      icon: <MedicineBoxOutlined />,
      label: '智能诊疗',
      onClick: () => navigate('/diagnosis'),
    },
    {
      key: 'tools',
      icon: <ToolOutlined />,
      label: '智能工具',
      onClick: () => navigate('/tools'),
    },
    {
      key: 'about',
      icon: <InfoCircleOutlined />,
      label: '关于我们',
      onClick: () => navigate('/about'),
    },
  ]

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'admin',
      icon: <AppstoreOutlined />,
      label: '管理后台',
      onClick: () => window.location.assign(getRuoyiAdminIndexUrl()),
    },
    {
      key: 'profile',
      icon: <IdcardOutlined />,
      label: '我的资料',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        dispatch(logout())
        navigate('/auth/login')
      },
    },
  ]

  const handleSearch = () => {
    console.log('Search:', searchValue)
  }

  return (
    <Header className="app-header">
      <div className="header-content">
        <div className="header-left">
          <div className="logo" onClick={() => navigate('/')}>
            <img
              src="/images/logo.png"
              alt="岐黄智医"
              className="logo-image"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <span className="logo-text">岐黄智医</span>
          </div>
          <Menu
            mode="horizontal"
            selectedKeys={[current]}
            items={menuItems}
            className="header-menu"
            overflowedIndicator={null}
            triggerSubMenuAction="hover"
            style={{ border: 'none', lineHeight: '64px', minWidth: 'fit-content' }}
          />
        </div>
        <div className="header-right">
          <Space.Compact style={{ width: 250, marginRight: 16 }}>
            <Input
              placeholder="搜索..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
              allowClear
              onClear={() => setSearchValue('')}
            />
            <Button icon={<SearchOutlined />} onClick={handleSearch} type="primary" />
          </Space.Compact>
          {isAuthenticated ? (
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
              dropdownRender={(menu) => (
                <div className="user-dropdown-panel">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-name">{user?.username}</div>
                    {user?.email ? <div className="user-dropdown-email">{user.email}</div> : null}
                  </div>
                  {menu}
                </div>
              )}
            >
              <div className="user-info" role="button" tabIndex={0}>
                <Avatar icon={<UserOutlined />} src={user?.avatar} />
                <span className="username">{user?.username}</span>
              </div>
            </Dropdown>
          ) : (
            <Button type="primary" onClick={() => navigate('/auth/login')}>
              登录
            </Button>
          )}
        </div>
      </div>
    </Header>
  )
}

export default AppHeader
